import { useState, useEffect } from 'react';
import API, { SERVER_URL } from '../../api/axios';
import AppLayout from '../../components/layout/AppLayout';
import { S } from '../../components/pageStyles';
import { Loader } from '../../components/ui';
import { LuBeer, LuBuilding, LuBuilding2, LuCircleCheck, LuClipboardList, LuClock, LuCreditCard, LuFileText, LuFolderOpen, LuGift, LuGlobe, LuImage, LuLocateFixed, LuMapPin, LuMoon, LuPackage, LuPencil, LuPhone, LuShoppingBag, LuStore, LuSunrise, LuUser, LuUtensils, LuWine } from 'react-icons/lu';

import { fmt } from '../../theme';
// ── COMPOSANT BADGE ABONNEMENT ───────────────────────────
function AbonnementBadge({ etablissementId }) {
  const [abo, setAbo] = useState(null);

  useEffect(() => {
    API.get('/abonnements/' + etablissementId)
      .then(res => setAbo(res.data.abonnement))
      .catch(() => {});
  }, [etablissementId]);

  if (!abo) return null;

  const joursRestants = Math.ceil(
    (new Date(abo.date_fin) - new Date()) / (1000 * 60 * 60 * 24)
  );

  const couleur = joursRestants <= 0 ? '#DC2626'
    : joursRestants <= 7 ? '#EA580C'
    : abo.statut === 'gratuit' ? '#2563EB'
    : '#059669';

  const bg = joursRestants <= 0 ? '#FEF2F2'
    : joursRestants <= 7 ? '#FFF7ED'
    : abo.statut === 'gratuit' ? '#EFF6FF'
    : '#ECFDF5';

  return (
    <div style={{
      padding: '10px 12px', borderRadius: 10,
      background: bg, marginBottom: 12,
      border: '1px solid ' + couleur + '40'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontWeight: 700, color: couleur, fontSize: 13 }}>
            {' ' + abo.plan_nom + (abo.statut === 'gratuit' ? ' ' : '')}
          </span>
          <div style={{ color: '#64748B', fontSize: 11, marginTop: 2 }}>
            {fmt(parseInt(abo.prix_mensuel || 0)) + ' FCFA/mois'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, color: couleur, fontSize: 14 }}>
            {joursRestants <= 0 ? 'Expiré'
              : joursRestants <= 7 ? (' ' + joursRestants + 'j')
              : (' ' + joursRestants + 'j')}
          </div>
          <div style={{ color: '#64748B', fontSize: 10 }}>restants</div>
        </div>
      </div>
    </div>
  );
}

// ── COMPOSANT PRINCIPAL ──────────────────────────────────
export default function Etablissements() {
  const [etablissements, setEtablissements] = useState([]);
  const [plans, setPlans] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [vue, setVue] = useState('liste');
  const [etabSelectionne, setEtabSelectionne] = useState(null);
  const [message, setMessage] = useState('');
  const [localisation, setLocalisation] = useState(false);
  const [page, setPage] = useState(1);
  const parPage = 2; // nombre de propriétaires par page

  const [form, setForm] = useState({
    nom: '', type: 'bar', telephone: '',
    adresse: '', latitude: '', longitude: '',
    secteur_activite: '', description: '',
    nom_responsable: '', proprietaire_id: '', horaire_ouverture: '',
    horaire_fermeture: '', plan_id: '1',
    secteur_stock: 'general'
  });

  useEffect(() => { chargerDonnees(); }, []);

 const chargerDonnees = async () => {
  try {
    const [etabRes, plansRes, usersRes] = await Promise.all([
      API.get('/etablissements'),
      API.get('/abonnements/plans'),
      API.get('/utilisateurs')
    ]);
    setEtablissements(etabRes.data.etablissements || []);
    setPlans(plansRes.data.plans || []);
    setUtilisateurs(usersRes.data.utilisateurs || []);
    } catch (err) {
      console.error(err);
    } finally {
      setChargement(false);
    }
  };

  // ── REGROUPEMENT PAR PROPRIÉTAIRE ────────────────────
  const grouperParProprietaire = () => {
    const groupes = {};
    etablissements.forEach(etab => {
      const proprietaire = etab.nom_responsable || 'Sans propriétaire';
      if (!groupes[proprietaire]) groupes[proprietaire] = [];
      groupes[proprietaire].push(etab);
    });
    return Object.entries(groupes).sort((a, b) => a[0].localeCompare(b[0]));
  };

  const groupes = grouperParProprietaire();
  const totalPages = Math.ceil(groupes.length / parPage);
  const groupesPage = groupes.slice((page - 1) * parPage, page * parPage);

  const obtenirLocalisation = () => {
    setLocalisation(true);
    if (!navigator.geolocation) {
      setMessage('❌ Géolocalisation non supportée');
      setLocalisation(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm(f => ({
          ...f,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        }));
        setLocalisation(false);
        setMessage('✅ Position détectée !');
        setTimeout(() => setMessage(''), 3000);
      },
      (err) => {
        setMessage('❌ Position introuvable : ' + err.message);
        setLocalisation(false);
        setTimeout(() => setMessage(''), 4000);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (etabSelectionne) {
        await API.put('/etablissements/' + etabSelectionne.id, form);
        setMessage('✅ Établissement modifié avec succès !');
      } else {
        await API.post('/etablissements', form);
        setMessage('✅ Établissement créé ! 2 mois gratuits activés 🎁');
      }
      chargerDonnees();
      setVue('liste');
      setEtabSelectionne(null);
      resetForm();
      setTimeout(() => setMessage(''), 4000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const handleToggleActif = async (etab) => {
    const action = etab.actif ? 'désactiver' : 'activer';
    if (!window.confirm('Voulez-vous ' + action + ' cet établissement ?')) return;
    try {
      await API.put('/etablissements/' + etab.id, { actif: !etab.actif });
      setMessage('✅ Établissement ' + (etab.actif ? 'désactivé' : 'activé') + ' !');
      chargerDonnees();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    }
  };

  const handleModifier = (etab) => {
    setEtabSelectionne(etab);
    setForm({
      nom: etab.nom || '',
      type: etab.type || 'bar',
      telephone: etab.telephone || '',
      adresse: etab.adresse || '',
      latitude: etab.latitude || '',
      longitude: etab.longitude || '',
      secteur_activite: etab.secteur_activite || '',
      description: etab.description || '',
      nom_responsable: etab.nom_responsable || '',
      proprietaire_id: etab.proprietaire_id || '',
      horaire_ouverture: etab.horaire_ouverture || '',
      horaire_fermeture: etab.horaire_fermeture || '',
      plan_id: '1',
      secteur_stock: etab.secteur_stock || 'general'
    });
    setVue('formulaire');
  };

  const resetForm = () => {
    setForm({
      nom: '', type: 'bar', telephone: '',
      adresse: '', latitude: '', longitude: '',
      secteur_activite: '', description: '',
      nom_responsable: '',proprietaire_id: '', horaire_ouverture: '',
      horaire_fermeture: '', plan_id: '1',
      secteur_stock: 'general'
    });
  };

  const typeIcons = { bar: <LuBeer aria-hidden />, boutique: <LuShoppingBag aria-hidden />, cave: <LuWine aria-hidden />, maquis: <LuUtensils aria-hidden />, depot: <LuPackage aria-hidden /> };

  const secteurStockLabels = {
    boissons: 'Boissons',
    restaurant: 'Restaurant',
    vetements: 'Vêtements',
    cosmetiques: 'Cosmétiques',
    general: 'Général',
    gaz: 'Gaz Butane'
  };

  if (chargement) return <Loader />;

  return (
    <AppLayout role="admin">

      {/* CONTENU */}
      <div style={styles.contenu}>

        {message && (
          <div role="status" style={{
            ...styles.messageBox,
            background: message.startsWith('✅') ? '#ECFDF5' : '#FEF2F2',
            color: message.startsWith('✅') ? '#047857' : '#B91C1C'
          }}>
            {message.replace(/^(✅|❌|⚠️)\s*/, '')}
          </div>
        )}

        {/* HEADER */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitre}>Établissements</h1>
            <p style={styles.headerSousTitre}>
              {etablissements.length + ' établissement(s) • ' +
               etablissements.filter(e => e.actif).length + ' actif(s) • ' +
               groupes.length + ' propriétaire(s)'}
            </p>
          </div>
          {vue === 'liste' ? (
            <button
              onClick={() => { resetForm(); setEtabSelectionne(null); setVue('formulaire'); }}
              style={styles.boutonAjouter}
            >
              + Nouvel Établissement
            </button>
          ) : (
            <button onClick={() => setVue('liste')} style={styles.boutonRetour}>
              ← Retour à la liste
            </button>
          )}
        </div>

        {/* ── LISTE GROUPÉE ── */}
        {vue === 'liste' && (
          <>
            {etablissements.length === 0 ? (
              <div style={styles.vide}>
                <div style={{ fontSize: 64 }}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /></div>
                <p style={{ fontSize: 18, marginBottom: 16 }}>Aucun établissement créé</p>
                <button onClick={() => setVue('formulaire')} style={styles.boutonAjouter}>
                  + Créer le premier établissement
                </button>
              </div>
            ) : (
              <>
                {groupesPage.map(([proprietaire, etabs], gi) => (
                  <div key={gi} style={styles.groupeSection}>

                    {/* EN-TÊTE PROPRIÉTAIRE */}
                    <div style={styles.groupeHeader}>
                      <div style={styles.groupeAvatar}>
                        {proprietaire[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div style={styles.groupeNom}>{' ' + proprietaire}</div>
                        <div style={styles.groupeStats}>
                          {etabs.length + ' établissement(s) • ' +
                           etabs.filter(e => e.actif).length + ' actif(s)'}
                        </div>
                      </div>
                    </div>

                    {/* CARTES DES ÉTABLISSEMENTS */}
                    <div style={styles.grille}>
                      {etabs.map((etab, i) => (
                        <div key={i} style={{
                          ...styles.etabCard,
                          opacity: etab.actif ? 1 : 0.7,
                          borderTop: '4px solid ' + (etab.actif ? '#059669' : '#DC2626')
                        }}>
                          <div style={styles.etabCardHeader}>
                            <span style={{ fontSize: 36 }}>
                              {typeIcons[etab.type] || <LuStore aria-hidden />}
                            </span>
                            <div style={{
                              ...styles.etabStatut,
                              background: etab.actif ? '#ECFDF5' : '#FEF2F2',
                              color: etab.actif ? '#059669' : '#DC2626'
                            }}>
                              {etab.actif ? 'Actif' : 'Inactif'}
                            </div>
                          </div>

                          <div style={styles.etabCardNom}>{etab.nom}</div>
                          <div style={styles.etabCardType}>
                            {etab.type + ' • ' + (etab.secteur_activite || 'N/A')}
                          </div>

                          {etab.secteur_stock && etab.secteur_stock !== 'general' && (
                            <div style={styles.secteurStockBadge}>
                              {secteurStockLabels[etab.secteur_stock] || 'Général'}
                            </div>
                          )}

                          {etab.telephone && (
                            <div style={styles.etabCardInfo}>{' ' + etab.telephone}</div>
                          )}
                          {etab.adresse && (
                            <div style={styles.etabCardInfo}>{' ' + etab.adresse}</div>
                          )}
                          {etab.latitude && (
                            <div style={styles.etabCardInfo}>
                              {' ' + parseFloat(etab.latitude).toFixed(4) +
                               ', ' + parseFloat(etab.longitude).toFixed(4)}
                            </div>
                          )}

                          <AbonnementBadge etablissementId={etab.id} />

                          <div style={styles.etabCardStats}>
                            <div style={styles.etabCardStat}>
                              <span style={styles.etabCardStatVal}>{etab.nombre_employes || 0}</span>
                              <span style={styles.etabCardStatLabel}>Employés</span>
                            </div>
                            <div style={styles.etabCardStat}>
                              <span style={styles.etabCardStatVal}>{etab.nombre_produits || 0}</span>
                              <span style={styles.etabCardStatLabel}>Produits</span>
                            </div>
                          </div>

                          <div style={styles.etabCardActions}>
                            <button onClick={() => handleModifier(etab)} style={styles.btnModifier}>
                              <LuPencil aria-hidden style={{ verticalAlign: '-0.125em' }} /> Modifier
                            </button>
                            <button
                              onClick={() => handleToggleActif(etab)}
                              style={{
                                ...styles.btnToggle,
                                background: etab.actif ? '#FEF2F2' : '#ECFDF5',
                                color: etab.actif ? '#DC2626' : '#059669'
                              }}
                            >
                              {etab.actif ? 'Désactiver' : 'Activer'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* PAGINATION */}
                {totalPages > 1 && (
                  <div style={styles.pagination}>
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{ ...styles.pagBtn, opacity: page === 1 ? 0.4 : 1 }}
                    >
                      ← Précédent
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button key={i} onClick={() => setPage(i + 1)} style={{
                        ...styles.pagNum,
                        background: page === i + 1 ? 'var(--accent)' : 'white',
                        color: page === i + 1 ? 'white' : '#475569'
                      }}>
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      style={{ ...styles.pagBtn, opacity: page === totalPages ? 0.4 : 1 }}
                    >
                      Suivant →
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── FORMULAIRE ── */}
        {vue === 'formulaire' && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitre}>
              {etabSelectionne ? "Modifier l'établissement" : 'Nouvel Établissement'}
            </h2>

            <form onSubmit={handleSubmit}>

              {/* SECTION 1 — INFOS GÉNÉRALES */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuClipboardList aria-hidden style={{ verticalAlign: '-0.125em' }} /> Informations Générales</div>
                <div style={styles.formGrid}>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuStore aria-hidden style={{ verticalAlign: '-0.125em' }} /> Nom de l'établissement *</label>
                    <input
                      type="text"
                      value={form.nom}
                      onChange={(e) => setForm({ ...form, nom: e.target.value })}
                      style={styles.input}
                      placeholder="Ex: Bar Le Phare"
                      required
                    />
                  </div>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuFolderOpen aria-hidden style={{ verticalAlign: '-0.125em' }} /> Type d'établissement *</label>
                    <select
                      value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value })}
                      style={styles.select}
                    >
                      <option value="bar">Bar</option>
                      <option value="boutique">Boutique</option>
                      <option value="cave">Cave</option>
                      <option value="maquis">Maquis</option>
                       <option value="depot">Dépôt</option>  
                    </select>
                  </div>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuUser aria-hidden style={{ verticalAlign: '-0.125em' }} /> Nom du propriétaire</label>
                    <input
                      type="text"
                      value={form.nom_responsable}
                      onChange={(e) => {
                        const valeur = e.target.value;
                        setForm({ ...form, nom_responsable: valeur });
                        // Récupérer l'id du propriétaire si le nom correspond
                        const userTrouve = utilisateurs.find(
                          u => (u.prenom + ' ' + u.nom) === valeur
                        );
                        if (userTrouve) {
                          setForm({ ...form, nom_responsable: valeur, proprietaire_id: userTrouve.id });
                        }
                      }}
                      style={styles.input}
                      placeholder="Ex: Jean Kouassi"
                      list="liste-proprietaires"
                    />
                    <datalist id="liste-proprietaires">
                      {utilisateurs
                        .filter(u => u.role === 'proprietaire' || u.role === 'admin')
                        .map((u, i) => (
                          <option key={i} value={u.prenom + ' ' + u.nom} />
                        ))
                      }
                    </datalist>
                    {form.proprietaire_id && (
                      <div style={{ color: '#059669', fontSize: 11, marginTop: 4 }}>
                        {'Propriétaire lié — ID : ' + form.proprietaire_id}
                      </div>
                    )}
                  </div>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuPhone aria-hidden style={{ verticalAlign: '-0.125em' }} /> Téléphone</label>
                    <input
                      type="tel"
                      value={form.telephone}
                      onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                      style={styles.input}
                      placeholder="07 00 00 00 00"
                    />
                  </div>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuBuilding2 aria-hidden style={{ verticalAlign: '-0.125em' }} /> Secteur d'activité</label>
                    <input
                      type="text"
                      value={form.secteur_activite}
                      onChange={(e) => setForm({ ...form, secteur_activite: e.target.value })}
                      style={styles.input}
                      placeholder="Ex: Boissons et cocktails"
                    />
                  </div>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuPackage aria-hidden style={{ verticalAlign: '-0.125em' }} /> Secteur de stock</label>
                   <select
                    value={form.secteur_stock}
                    onChange={(e) => setForm({ ...form, secteur_stock: e.target.value })}
                    style={styles.select}
                  >
                    <option value="general">Général</option>
                    <option value="boissons">Boissons (Bar/Cave/Lounge)</option>
                    <option value="restaurant">Restaurant/Maquis</option>
                    <option value="vetements">Vêtements</option>
                    <option value="cosmetiques">Cosmétiques</option>
                    <option value="gaz">Gaz Butane Domestique</option>
                  </select>
                  </div>

                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuImage aria-hidden style={{ verticalAlign: '-0.125em' }} /> Logo de l'établissement</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setForm({ ...form, logo: e.target.files[0] })}
                          style={styles.input}
                        />
                    {etabSelectionne?.logo && (
                      <img
                        src={SERVER_URL + '/' + etabSelectionne.logo}
                        alt="Logo actuel"
                        style={{ width: 80, height: 80, borderRadius: 8, marginTop: 6, objectFit: 'cover' }}
                      />
                    )}
                  </div>

                </div>
              </div>

              {/* SECTION 2 — HORAIRES */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuClock aria-hidden style={{ verticalAlign: '-0.125em' }} /> Horaires d'ouverture</div>
                <div style={styles.formGrid}>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuSunrise aria-hidden style={{ verticalAlign: '-0.125em' }} /> Heure d'ouverture</label>
                    <input
                      type="time"
                      value={form.horaire_ouverture}
                      onChange={(e) => setForm({ ...form, horaire_ouverture: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                  <div style={styles.champGroupe}>
                    <label style={styles.label}><LuMoon aria-hidden style={{ verticalAlign: '-0.125em' }} /> Heure de fermeture</label>
                    <input
                      type="time"
                      value={form.horaire_fermeture}
                      onChange={(e) => setForm({ ...form, horaire_fermeture: e.target.value })}
                      style={styles.input}
                    />
                  </div>
                </div>
              </div>

              {/* SECTION 3 — LOCALISATION */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuMapPin aria-hidden style={{ verticalAlign: '-0.125em' }} /> Localisation</div>
                <div style={styles.champGroupe}>
                  <label style={styles.label}><LuBuilding aria-hidden style={{ verticalAlign: '-0.125em' }} /> Adresse complète</label>
                  <input
                    type="text"
                    value={form.adresse}
                    onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                    style={{ ...styles.input, marginBottom: 12 }}
                    placeholder="Ex: Cocody, Abidjan, Côte d'Ivoire"
                  />
                </div>
                <div style={styles.localisationBox}>
                  <div style={styles.formGrid}>
                    <div style={styles.champGroupe}>
                      <label style={styles.label}><LuGlobe aria-hidden style={{ verticalAlign: '-0.125em' }} /> Latitude</label>
                      <input
                        type="number" step="any"
                        value={form.latitude}
                        onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                        style={styles.input}
                        placeholder="Ex: 5.360000"
                      />
                    </div>
                    <div style={styles.champGroupe}>
                      <label style={styles.label}><LuGlobe aria-hidden style={{ verticalAlign: '-0.125em' }} /> Longitude</label>
                      <input
                        type="number" step="any"
                        value={form.longitude}
                        onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                        style={styles.input}
                        placeholder="Ex: -4.008300"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={obtenirLocalisation}
                    disabled={localisation}
                    style={styles.boutonGeo}
                  >
                    {localisation ? 'Détection en cours…' : <><LuLocateFixed aria-hidden /> Détecter ma position automatiquement</>}
                  </button>
                {form.latitude && form.longitude && (
                    <div style={styles.coordonnees}>
                        <span>
                        {'Position : ' + parseFloat(form.latitude).toFixed(4) + ', ' + parseFloat(form.longitude).toFixed(4)}
                      </span>
                      <a href={'https://maps.google.com/?q=' + form.latitude + ',' + form.longitude} target="_blank" rel="noreferrer" style={styles.voirCarte}>
                        {'Voir sur la carte'}
                      </a>
                    
                    </div>
                  )}
                </div>
              </div>

              {/* SECTION 4 — ABONNEMENT */}
              {!etabSelectionne && (
                <div style={styles.sectionForm}>
                  <div style={styles.sectionFormTitre}>
                    <LuCreditCard aria-hidden style={{ verticalAlign: '-0.125em' }} /> Plan d'abonnement
                    <span style={styles.gratuitBadge}><LuGift aria-hidden style={{ verticalAlign: '-0.125em' }} /> 2 mois gratuits !</span>
                  </div>
                  <div style={styles.plansGrid}>
                    {plans.map((plan) => (
                      <div
                        key={plan.id}
                        onClick={() => setForm({ ...form, plan_id: plan.id.toString() })}
                        style={{
                          ...styles.planCard,
                          border: form.plan_id === plan.id.toString()
                            ? '2px solid #059669' : '2px solid #E2E8F0',
                          background: form.plan_id === plan.id.toString()
                            ? '#F0FFF4' : 'white'
                        }}
                      >
                        <div style={styles.planNom}>{plan.nom}</div>
                        <div style={styles.planPrix}>
                          {fmt(parseInt(plan.prix_mensuel))}
                          <span style={{ fontSize: 12, color: '#64748B' }}> FCFA/mois</span>
                        </div>
                        <div style={styles.planLimites}>
                          <div>{' ' + (plan.max_produits || '∞') + ' produits'}</div>
                          <div>{' ' + (plan.max_utilisateurs || '∞') + ' utilisateurs'}</div>
                        </div>
                        <div style={styles.planDesc}>{plan.description}</div>
                        {form.plan_id === plan.id.toString() && (
                          <div style={styles.planSelectionne}><LuCircleCheck aria-hidden style={{ verticalAlign: '-0.125em' }} /> Sélectionné</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION 5 — DESCRIPTION */}
              <div style={styles.sectionForm}>
                <div style={styles.sectionFormTitre}><LuFileText aria-hidden style={{ verticalAlign: '-0.125em' }} /> Description</div>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  style={{ ...styles.input, height: 100, resize: 'vertical' }}
                  placeholder="Description de l'établissement..."
                />
              </div>

              {/* ACTIONS */}
              <div style={styles.formActions}>
                <button type="button" onClick={() => setVue('liste')} style={styles.btnAnnuler}>
                  Annuler
                </button>
                <button type="submit" style={styles.btnSauvegarder}>
                  {etabSelectionne ? 'Sauvegarder les modifications' : "Créer l'établissement"}
                </button>
              </div>

            </form>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

const styles = {
  chargement: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', height: '100vh',
    fontSize: 24, color: '#475569'
  },
  contenu: { ...S.contenu, flex: 1 },
  messageBox: { ...S.messageBox, marginBottom: 16 },
  header: S.header,
  headerTitre: S.headerTitre,
  headerSousTitre: S.headerSousTitre,
  boutonAjouter: S.boutonAjouter,
  boutonRetour: S.boutonRetour,
  groupeSection: {
    marginBottom: 32
  },
  groupeHeader: {
    display: 'flex', alignItems: 'center',
    gap: 14, marginBottom: 16,
    background: 'white', padding: '16px 20px',
    borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    borderLeft: '4px solid #2563EB'
  },
  groupeAvatar: {
    width: 44, height: 44, borderRadius: '50%',
    background: 'linear-gradient(135deg, #2563EB, #00B0FF)',
    display: 'flex', alignItems: 'center',
    justifyContent: 'center', color: 'white',
    fontWeight: 700, fontSize: 18, flexShrink: 0
  },
  groupeNom: { fontWeight: 700, fontSize: 16, color: '#0F172A' },
  groupeStats: { color: '#64748B', fontSize: 12, marginTop: 2 },
  grille: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 16
  },
  etabCard: {
    background: 'white', borderRadius: 16,
    padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  etabCardHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 12
  },
  etabStatut: {
    padding: '4px 12px', borderRadius: 20,
    fontSize: 12, fontWeight: 700
  },
  etabCardNom: { fontSize: 17, fontWeight: 700, color: '#0F172A', marginBottom: 4 },
  etabCardType: { color: '#475569', fontSize: 13, marginBottom: 4 },
  secteurStockBadge: {
    display: 'inline-block', marginBottom: 8,
    padding: '3px 10px', borderRadius: 20,
    background: '#EFF6FF', color: '#2563EB',
    fontSize: 11, fontWeight: 700
  },
  etabCardInfo: { color: '#64748B', fontSize: 12, marginBottom: 4 },
  etabCardStats: {
    display: 'flex', gap: 16,
    padding: '12px 0', margin: '8px 0',
    borderTop: '1px solid #F1F5F9',
    borderBottom: '1px solid #F1F5F9'
  },
  etabCardStat: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  etabCardStatVal: { fontWeight: 700, fontSize: 18, color: '#0F172A' },
  etabCardStatLabel: { color: '#64748B', fontSize: 11 },
  etabCardActions: { display: 'flex', gap: 8, marginTop: 12 },
  btnModifier: { ...S.btnModifier, flex: 1 },
  btnToggle: {
    flex: 1, padding: '8px',
    border: 'none', borderRadius: 8,
    cursor: 'pointer', fontWeight: 700, fontSize: 13
  },
  vide: S.vide,
  pagination: { ...S.pagination, marginTop: 24 },
  pagBtn: S.pagBtn,
  pagNum: { ...S.pagNum, width: 32 },
  formCard: S.formCard,
  formTitre: { ...S.formTitre, marginBottom: 24 },
  sectionForm: { ...S.sectionForm, marginBottom: 28 },
  sectionFormTitre: { ...S.sectionFormTitre, marginBottom: 16 },
  formGrid: S.formGrid,
  champGroupe: S.champGroupe,
  label: S.label,
  input: { ...S.input, width: '100%' },
  select: { ...S.select, width: '100%' },
  localisationBox: {
    background: '#EFF6FF', borderRadius: 10,
    padding: 16, marginTop: 8
  },
  boutonGeo: {
    width: '100%', padding: '12px',
    background: 'linear-gradient(135deg, #2563EB, #00B0FF)',
    color: 'white', border: 'none', borderRadius: 10,
    cursor: 'pointer', fontWeight: 700, fontSize: 14,
    marginTop: 12, marginBottom: 8
  },
  coordonnees: {
    background: '#ECFDF5', color: '#059669',
    padding: '10px 14px', borderRadius: 8,
    fontSize: 13, fontWeight: 700,
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between'
  },
  voirCarte: {
    color: '#2563EB', fontSize: 12,
    textDecoration: 'none', fontWeight: 700
  },
  gratuitBadge: {
    background: 'var(--accent)',
    color: 'white', padding: '3px 10px',
    borderRadius: 20, fontSize: 12,
    fontWeight: 700, marginLeft: 8
  },
  plansGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16
  },
  planCard: {
    padding: 16, borderRadius: 12,
    cursor: 'pointer', textAlign: 'center',
    transition: 'all 0.2s'
  },
  planNom: { fontWeight: 700, fontSize: 16, marginBottom: 8 },
  planPrix: { fontSize: 22, fontWeight: 700, color: '#059669', marginBottom: 8 },
  planLimites: { color: '#475569', fontSize: 12, lineHeight: 1.8, marginBottom: 6 },
  planDesc: { color: '#64748B', fontSize: 12 },
  planSelectionne: { marginTop: 8, color: '#059669', fontWeight: 700, fontSize: 13 },
  formActions: { ...S.formActions, marginTop: 16 },
  btnAnnuler: S.btnAnnuler,
  btnSauvegarder: S.btnSauvegarder
};